import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

// Réassignation en masse des clients d'un commercial vers un autre (page admin SUPER_ADMIN).
// Miroir des endpoints boostcore `companies/by-commercial` et `companies/bulk-reassign-commercial`.

export interface ReassignClientRow {
  uuid: string
  name: string
  siren: string | null
  zip: string | null
}

/** Option commercial pour les sélecteurs source / cible. */
export interface CommercialOption {
  uuid: string
  email: string
  firstName: string | null
  lastName: string | null
  label: string
}

export interface BulkReassignResult {
  updated: number
  sellerAssignments: number
}

export const useClientReassignStore = defineStore('clientReassign', () => {
  const suiteStore = useSuiteStore()

  const clients = ref<ReassignClientRow[]>([])
  const total = ref(0)
  const commercials = ref<CommercialOption[]>([])
  const isLoadingClients = ref(false)
  const isLoadingCommercials = ref(false)
  const isReassigning = ref(false)

  function buildLabel(u: { firstName: string | null; lastName: string | null; email: string }): string {
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ')
    return name || u.email
  }

  /** Liste tous les utilisateurs (source/cible) via l'endpoint admin existant. */
  async function fetchCommercials(): Promise<CommercialOption[]> {
    isLoadingCommercials.value = true
    try {
      const res = await suiteStore.suiteFetch('users/admin/list?limit=500', {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        const json = await res.json()
        const data: Array<{ uuid: string; email: string; firstName: string | null; lastName: string | null }> =
          json.data ?? []
        commercials.value = data
          .map((u) => ({ ...u, label: buildLabel(u) }))
          .sort((a, b) => a.label.localeCompare(b.label, 'fr'))
        return commercials.value
      }
    } catch (e) {
      console.error('Erreur récupération des commerciaux', e)
    } finally {
      isLoadingCommercials.value = false
    }
    return []
  }

  /** Liste les clients assignés à un commercial source. */
  async function fetchClientsByCommercial(commercialUuid: string, search?: string): Promise<ReassignClientRow[]> {
    isLoadingClients.value = true
    try {
      const res = await suiteStore.suiteFetch('companies/by-commercial', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignedCommercialUuid: commercialUuid, search: search?.trim() || undefined }),
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        const json = await res.json()
        clients.value = json.data ?? []
        total.value = json.total ?? clients.value.length
        return clients.value
      }
    } catch (e) {
      console.error('Erreur récupération des clients du commercial', e)
    } finally {
      isLoadingClients.value = false
    }
    clients.value = []
    total.value = 0
    return []
  }

  /** Réassigne les clients sélectionnés vers le commercial cible. */
  async function bulkReassign(companyUuids: string[], targetCommercialUuid: string): Promise<BulkReassignResult | null> {
    isReassigning.value = true
    try {
      const res = await suiteStore.suiteFetch('companies/bulk-reassign-commercial', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyUuids, targetCommercialUuid }),
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        return (await res.json()) as BulkReassignResult
      }
    } catch (e) {
      console.error('Erreur réassignation des clients', e)
    } finally {
      isReassigning.value = false
    }
    return null
  }

  function reset() {
    clients.value = []
    total.value = 0
  }

  return {
    clients,
    total,
    commercials,
    isLoadingClients,
    isLoadingCommercials,
    isReassigning,
    fetchCommercials,
    fetchClientsByCommercial,
    bulkReassign,
    reset,
  }
})
